import { FiList } from 'react-icons/fi';

export const matchingHeadingsData = {
  courseId: 'matching-headings',
  courseType: 'reading-course',
  title: 'Matching Headings',
  icon: FiList,
  overview: {
    impact: 'Identify the main idea of each paragraph quickly and accurately',
    focus: 'Main idea recognition, paragraph mapping, heading elimination',
    uses: 'Reading, skimming, structure awareness',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Matching Headings Tests',
      content:
        '<p>This task is about <strong>main idea</strong>, not interesting details. IELTS wants to know whether you can summarize what a paragraph is mostly about.</p>',
      points: [
        'A good heading covers the paragraph as a whole.',
        'A wrong heading often matches one sentence but ignores the paragraph\'s full purpose.',
        'This task rewards skimming, not slow line-by-line reading.',
      ],
    },
    {
      title: 'A Four-Step Strategy',
      content: '<p>Use the headings and the paragraphs together:</p>',
      points: [
        'Read all headings first and paraphrase them into simpler language.',
        'Skim the paragraph for topic sentence, contrast words, and conclusion sentence.',
        'Ask: what is this paragraph doing? Introducing, contrasting, warning, explaining a cause, giving an example?',
        'Eliminate headings once you are confident, especially when options are similar.',
      ],
      warning:
        "Do not choose a heading just because one keyword appears. The heading must fit the whole paragraph.",
    },
    {
      title: 'Common Distractor Patterns',
      content: '<p>Wrong headings are often wrong in predictable ways:</p>',
      table: {
        headers: ['Distractor Type', 'Example of the Problem'],
        rows: [
          ['Too specific', 'Matches one example but not the whole paragraph'],
          ['Too broad', 'Could fit the article, but not that paragraph'],
          ['Wrong angle', 'Paragraph is about causes, heading is about solutions'],
          ['Shared vocabulary', 'Same words, different idea'],
          ['Chronology trap', 'Heading focuses on time order when paragraph focuses on comparison'],
        ],
      },
    },
    {
      title: 'What to Read First',
      content: '<p>In most paragraphs, these are the highest-value lines:</p>',
      points: [
        'The first sentence often introduces the topic or claim.',
        'The last sentence often gives the conclusion, shift, or main takeaway.',
        'Repeated nouns or repeated contrast markers show the paragraph\'s center of gravity.',
        'Examples, names, and dates matter less unless they support the paragraph\'s main idea.',
      ],
    }
  ],
  exercises: [
    {
      title: 'Heading Logic Check',
      type: 'select',
      instructions: 'Choose the best heading for each paragraph summary.',
      questions: [
        {
          text:
            'Paragraph: "Although electric cars are often praised for low emissions, their overall impact depends heavily on how the electricity is produced. In regions powered by coal, their environmental advantage is smaller than many consumers assume."\nWhat is the best heading?',
          options: [
            'The hidden limits of a popular solution',
            'How electric cars became affordable',
            'A history of public transport policy'
          ],
          answer: 'The hidden limits of a popular solution',
          explanation:
            'The paragraph challenges a widely praised idea by showing its limits.',
        },
        {
          text:
            'Paragraph: "In the first phase, researchers interviewed local farmers. They then tested soil samples in three regions before publishing recommendations for crop rotation."\nWhat is the best heading?',
          options: [
            'The stages of a research project',
            'Why crop rotation always fails',
            'Farmers reject scientific advice'
          ],
          answer: 'The stages of a research project',
          explanation:
            'The paragraph is organized as a sequence of steps.',
        },
        {
          text:
            'Paragraph: "Many office workers report fatigue by late afternoon. However, companies that redesigned lighting and break schedules saw concentration levels improve within weeks."\nWhat is the best heading?',
          options: [
            'A practical response to a productivity problem',
            'Why employees refuse to take breaks',
            'The history of workplace lighting'
          ],
          answer: 'A practical response to a productivity problem',
          explanation:
            'The paragraph presents a problem, then highlights an effective intervention.',
        },
      ],
    },
    {
      title: 'Too Specific or Just Right?',
      type: 'select',
      instructions: 'Decide which option captures the paragraph best.',
      questions: [
        {
          text:
            'Paragraph focus: a comparison between city gardens and rooftop farms as two ways to increase food production in urban areas.\nWhich heading works best?',
          options: [
            'Two urban methods compared',
            'How tomatoes grow on rooftops',
            'The complete history of urban design'
          ],
          answer: 'Two urban methods compared',
          explanation:
            'The paragraph compares two methods; the tomato option is too specific.',
        },
        {
          text:
            'Paragraph focus: a warning that early AI models produced confident but inaccurate medical suggestions.\nWhich heading works best?',
          options: [
            'The risks of trusting early systems too much',
            'How doctors are trained',
            'The growth of hospital buildings'
          ],
          answer: 'The risks of trusting early systems too much',
          explanation:
            'This heading captures the warning and the central message of over-trust.',
        },
      ],
    },
    {
      title: 'Heading Elimination Practice',
      type: 'fill-in',
      instructions: 'Type the best heading number.',
      questions: [
        {
          text:
            'Headings:\n1. A failed prediction\n2. Changing consumer habits\n3. A scientific explanation\n\nParagraph: "As streaming subscriptions became cheaper, fewer people bought DVDs. Retailers responded by shrinking physical media sections and increasing digital gift card sales."\nBest heading number:',
          answer: '2',
          explanation:
            'The paragraph is about a shift in how consumers buy media.',
        },
        {
          text:
            'Headings:\n1. The value of repetition\n2. Why migration patterns vary\n3. A misunderstanding corrected\n\nParagraph: "Earlier theories assumed birds followed the same route every year. Satellite data later showed several populations changed direction depending on weather conditions."\nBest heading number:',
          answer: '3',
          explanation:
            'The paragraph contrasts an earlier assumption with a later correction.',
        },
      ],
    },
    {
      title: 'Write the Main Idea',
      type: 'writing',
      instructions: 'Write a one-sentence heading in your own words.',
      questions: [
        {
          text:
            'Paragraph: "Several schools invested in tablets, but test scores changed little. Teachers later explained that the devices were used mostly for note-taking, not for redesigning lessons."\nWrite a heading.',
          example:
            'Technology alone does not improve learning outcomes.',
          tip:
            'A strong heading captures the conclusion, not the equipment detail.',
        },
        {
          text:
            'Paragraph: "The river now floods less often because wetlands upstream absorb seasonal rainfall. When those wetlands were previously drained, nearby towns faced heavier damage."\nWrite a heading.',
          example:
            'Wetlands reduce downstream flood risk.',
          tip:
            'Look for the cause-and-effect relationship that drives the paragraph.',
        },
      ],
    },
  ],
  tips: [
    'Paraphrase headings into simpler words before matching them.',
    'Look for the paragraph\'s job: define, compare, warn, explain, challenge, or conclude.',
    'Be suspicious of headings that fit only one example sentence.',
    'If two headings seem possible, choose the one that covers more of the paragraph.',
  ],
  dailyPractice: [
    'Read one article and write a 3-5 word heading for each paragraph.',
    'Practice spotting topic sentences and conclusion sentences.',
    'Take one paragraph and write both a good heading and a misleading distractor.',
    'Review wrong answers by asking whether your choice was too broad or too specific.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Matching Headings: finding the main idea of a paragraph, eliminating distractor headings, and reading for structure rather than detail.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...matchingHeadingsData,
      title: matchingHeadingsData.title + ' (Foundations)',
      overview: { ...matchingHeadingsData.overview, duration: '15-20 minutes' },
      sections: matchingHeadingsData.sections.slice(0, 2),
      exercises: [
        {
          ...matchingHeadingsData.exercises[0],
          title: 'Heading Logic Check (Basic)',
          questions: matchingHeadingsData.exercises[0].questions.slice(0, 2),
        },
        {
          ...matchingHeadingsData.exercises[2],
          title: 'Heading Elimination Practice (Basic)',
          questions: matchingHeadingsData.exercises[2].questions.slice(0, 1),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...matchingHeadingsData,
      title: matchingHeadingsData.title + ' (Advanced)',
      overview: { ...matchingHeadingsData.overview, duration: '30-35 minutes' },
      sections: [
        ...matchingHeadingsData.sections,
        {
          title: 'Advanced Similar-Heading Problems',
          content:
            '<p>At higher levels, the hardest part is choosing between two headings that both seem plausible. Focus on the paragraph\'s <strong>dominant message</strong>, not a minor theme.</p>',
          points: [
            'Ask which idea the paragraph returns to most strongly.',
            'Prefer the heading that fits the opening and closing sentences together.',
            'Reject headings that would need extra assumptions to work.',
          ],
        },
      ],
      exercises: [
        ...matchingHeadingsData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Close-Distractor Challenge',
          type: 'select',
          instructions: 'Choose the better of two very similar headings.',
          questions: [
            {
              text:
                'Paragraph: "The first generation of solar roofs was expensive and unreliable. Newer designs are cheaper, easier to install, and increasingly used in ordinary housing developments."\nWhich heading is better?',
              options: [
                'A technology becomes practical',
                'Why early inventors failed'
              ],
              answer: 'A technology becomes practical',
              explanation:
                'The paragraph starts with earlier problems but mainly emphasizes present practicality and adoption.',
            },
          ],
        },
      ],
      tips: [
        ...(matchingHeadingsData.tips || []),
        'At Band 8+, justify your choice by saying why the second-best heading is still wrong.',
        'When two headings overlap, compare their scope and purpose carefully.',
      ],
    };
  }
  return matchingHeadingsData;
}
